import React from "react";
import { Calendar, Clock, Shield } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/noel-a-tmnresourcesolutions/30min";

const PERKS = [
  { icon: Clock, label: "30 min intro", sub: "No-pressure discovery" },
  { icon: Calendar, label: "Fast scheduling", sub: "Slots this week" },
  { icon: Shield, label: "No commitment", sub: "Walk away any time" },
];

const CalendlyCTA = () => {
  return (
    <section
      id="booking"
      data-testid="calendly-section"
      aria-label="Book a strategy call"
      style={{
        position: "relative",
        padding: "96px 0 144px",
        background: "rgba(8,8,8,0.70)",
        overflow: "hidden",
      }}
    >
      {/* Decorative background */}
      <div style={{ position: "absolute", inset: 0 }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent, rgba(210,170,60,0.15), transparent)" }} />
        <div
          style={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            filter: "blur(120px)",
            background: "radial-gradient(circle, rgba(210,170,60,0.06) 0%, transparent 70%)",
            animation: "hero-orb-1 16s ease-in-out infinite",
          }}
        />
      </div>

      <div style={{ position: "relative", maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "64px", alignItems: "center" }}>
          {/* Left - content */}
          <div>
            <div
              className="reveal"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "10px",
                letterSpacing: "0.4em",
                color: "rgba(210,170,60,0.8)",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}
            >
              Book Your Call
            </div>
            <h2
              className="reveal reveal-delay-1"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(36px, 5vw, 60px)",
                fontWeight: 300,
                lineHeight: 1.08,
                color: "#fff",
                letterSpacing: "0.02em",
                marginBottom: "24px",
              }}
            >
              Let's fill your{" "}
              <em style={{ color: "#e8c45a", fontStyle: "italic", fontWeight: 400 }}>calendar</em>.
            </h2>
            <p
              className="reveal reveal-delay-2"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "14px",
                fontWeight: 300,
                color: "rgba(220,195,140,0.6)",
                lineHeight: 1.8,
                letterSpacing: "0.02em",
                marginBottom: "40px",
              }}
            >
              Grab a 30-minute strategy call. We'll audit your current pipeline,
              define your ideal appointment profile, and map out exactly what a
              done-for-you setup would look like for your business.
            </p>

            {/* Perks */}
            <div className="reveal reveal-delay-3" style={{ display: "flex", flexDirection: "column", gap: "20px", marginBottom: "40px" }}>
              {PERKS.map((p) => (
                <div key={p.label} style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      border: "1px solid rgba(210,170,60,0.2)",
                      background: "rgba(210,170,60,0.04)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <p.icon style={{ width: "16px", height: "16px", color: "#e8c45a" }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "16px", fontWeight: 400, color: "#fff" }}>{p.label}</div>
                    <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "11px", fontWeight: 300, color: "rgba(200,160,50,0.5)", letterSpacing: "0.05em" }}>{p.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="reveal reveal-delay-4">
              <button
                data-testid="calendly-open-button"
                onClick={() => window.open(CALENDLY_URL, "_blank", "noopener,noreferrer")}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "11px",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontWeight: 400,
                  padding: "16px 36px",
                  background: "rgba(210,165,40,0.15)",
                  border: "1px solid rgba(210,165,40,0.7)",
                  color: "#e8c45a",
                  cursor: "pointer",
                  transition: "all 0.35s",
                  borderRadius: "0",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(210,165,40,0.3)";
                  e.currentTarget.style.borderColor = "#e8c45a";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(210,165,40,0.15)";
                  e.currentTarget.style.borderColor = "rgba(210,165,40,0.7)";
                  e.currentTarget.style.color = "#e8c45a";
                }}
              >
                Open Full Calendar
              </button>
            </div>
          </div>

          {/* Right - Calendly embed */}
          <div className="reveal reveal-delay-2">
            <div
              style={{
                border: "1px solid rgba(210,170,60,0.15)",
                background: "rgba(8,8,8,0.8)",
                boxShadow: "0 0 80px rgba(210,170,60,0.05)",
              }}
            >
              <iframe
                data-testid="calendly-embed"
                title="TMN Resource Solutions — Strategy Call Booking"
                src={CALENDLY_URL + "?hide_event_type_details=1&hide_gdpr_banner=1&background_color=080808&text_color=ffffff&primary_color=D4AF37"}
                style={{ width: "100%", height: "640px", background: "#080808", border: "none" }}
                frameBorder="0"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalendlyCTA;
